import React, { useState, useEffect, useRef } from "react";
import { fetchUserMeetings } from "../api/xchange-api";
import {
  ScheduleComponent,
  Day,
  Week,
  Agenda,
  Resize,
  DragAndDrop,
  Month,
  ViewsDirective,
  ViewDirective,
  Inject,
  ResourcesDirective,
  ResourceDirective,
} from "@syncfusion/ej2-react-schedule";
import { L10n, createElement } from "@syncfusion/ej2-base";
import { ButtonComponent as Button } from "@syncfusion/ej2-react-buttons";
import { DropDownListComponent } from "@syncfusion/ej2-react-dropdowns";
import { DateTimePickerComponent } from "@syncfusion/ej2-react-calendars";
import "./ScheduleView.css";
import { MEETING_LOCATIONS } from "../utils/consts";
import { getPersonalConferanceURL } from "../utils/url-utils";
import { convertToLocalTime } from "../utils/time-utils";
import { logAction } from "../utils/log-utils";

const URL_NOT_AVAILABLE = "URL not available";
const URL_NOT_AVAILABLE_MESSAGE =
  "Can't connect to a meeting because URL not available.";

L10n.load({
  "en-US": {
    schedule: {
      saveButton: "Add",
      cancelButton: "Cancel",
      deleteButton: "Remove",
      editEvent: "Session",
    },
  },
});

export function ScheduleView({ ResourceData, SelectedHosts, hosts }) {
  const scheduleRef = useRef();
  const [AllEvents, setAllEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const updatedEvents = useRef([]);
  const hostsWhoseEventsFetched = useRef([]);

  useEffect(()  => {
    const fetchEventListForHost = async (hostID) => {
      try {
        let orderID = hosts.find((h) => h.Id === hostID)?.OrderID;
        const response = await fetchUserMeetings(orderID);
        let newEventsList = [];
        response.forEach(function (event) {
          let participantsList = [];
          event.PersonalEventsParticipants.forEach((participants) => {
            participantsList.push({
              Name: participants.Name || participants.NameOnBadge,
              Company: participants.Company,
              ProfilePhoto: participants.Profile
                ? participants.Profile.ProfilePhotoFullPath
                : null,
              Status: participants.Status,
            });
          });

          let ProjectList = [];
          event.PersonalEventsProjects.forEach((projectItem) => {
            ProjectList.push({
              ProjectID: projectItem.ProjectID,
              ProjectName: projectItem.Project.ProjectName,
              ImageLocation: projectItem.Project.ImageLocation,
            });
          });
          newEventsList.push({
            Id: hostID,
            HostId: hostID,
            EventID: event.EventID,
            UserID: hostID,
            Subject: event.EventName,
            Location: event.Location,
            Notes:
              event.PersonalEventSession && event.PersonalEventSession.Session
                ? event.PersonalEventSession.Session.Description
                : event.Notes,
            StartTime: convertToLocalTime(event.EventStartTime),
            EndTime: convertToLocalTime(event.EventEndTime),
            Participants: participantsList,
            Projects: ProjectList,
            XchangeMeetingURL: event.XchangeMeetingURL ?? URL_NOT_AVAILABLE,
            Session: event.PersonalEventSession,
          });
        });

        updatedEvents.current = [...updatedEvents.current, ...newEventsList];
        setAllEvents(updatedEvents.current);
      } catch (err) {
        console.error(err);
      }
    };

    let newlySelectedHosts = [];
    SelectedHosts.forEach( (host) => {
      if (!hostsWhoseEventsFetched.current.includes(host)) {
        fetchEventListForHost(host);
        newlySelectedHosts.push(host);
      }
    });

    hostsWhoseEventsFetched.current = [
      ...hostsWhoseEventsFetched.current,
      ...newlySelectedHosts,
    ];
  }, [SelectedHosts, hosts]);

  useEffect(() => {
    if (AllEvents.length > 0) {
      const filteredEvents = AllEvents.filter(
        (d) => SelectedHosts.indexOf(d.UserID) > -1
      );
      setFilteredEvents(filteredEvents);
    }
  }, [SelectedHosts, AllEvents]);

  const onPopupOpen = (args) => {
    if (args.data.Id) {
      if (args.type === "QuickInfo") {
        scheduleRef.current.openEditor(args.data, "Save", true);
        args.cancel = true;
      } else if (args.type === "Editor") {
        if (args.data.Session) {
          document.getElementsByClassName("e-title-text")[0].style.display = "";
          document.getElementById("custom-session-editor").style.display = "";
          document.getElementById("custom-event-editor").style.display = "none";

          var sessionStart = args.element.querySelector("#SessionDate");
          if (!sessionStart.classList.contains("e-datetimepicker")) {
            sessionStart.value = args.data.StartTime.toLocaleString("default", {
              weekday: "long",
              month: "long",
              year: "numeric",
              day: "numeric",
            });
          }

          var sessionStartTime = args.element.querySelector(
            "#SessionStartTime"
          );
          if (!sessionStartTime.classList.contains("e-datetimepicker")) {
            sessionStartTime.value = args.data.StartTime.toLocaleString(
              "default",
              { hour: "2-digit", minute: "2-digit", hour12: true }
            );
          }
          var SessionEndTime = args.element.querySelector("#SessionEndTime");
          if (!SessionEndTime.classList.contains("e-datetimepicker")) {
            SessionEndTime.value = args.data.EndTime.toLocaleString("default", {
              hour: "2-digit",
              minute: "2-digit",
              hour12: true,
            });
          }
          var SessionDescription = args.element.querySelector(
            "#SessionDescription"
          );
          SessionDescription.innerHTML = args.data.Notes;
        } else {
          document.getElementsByClassName("e-title-text")[0].style.display =
            "none";
          document.getElementById("custom-session-editor").style.display =
            "none";
          document.getElementById("custom-event-editor").style.display = "";

          args.element.querySelector("#XchangeMeetingURL").title =
            args.data.XchangeMeetingURL;
          let participantsElement = args.element.querySelector("#Participants");
          let ulContainer = createElement("ul", {
            className: "token-input-list",
          });

          args.data.Participants.forEach(function (participant) {
            let liContainer = createElement("li", {
              className: "token-input-token",
            });
            let pContainer = createElement("p", {
              attrs: { style: "font-weight: bold;" },
            });
            let imgProfile = createElement("img", {
              className: "small-profile",
              attrs: { src: participant.ProfilePhoto },
            });
            let nameContainer = createElement("span", {
              innerHTML: participant.Name,
            });
            let status = participant.Status;
            let StatusIcon =
              status === "Accepted"
                ? "green_checkmark"
                : status === "Declined"
                ? "red_x"
                : "black_questionmark";
            let imgStatus = createElement("img", {
              className: "meetingStatus",
              attrs: {
                src: `/img/icons/${StatusIcon}.png`,
                title: participant.Status,
              },
            });
            let organizerText = createElement("span", {
              className: "smalltextleft",
              innerHTML: " (Event Organizer)",
            });
            let organizationContainer = createElement("span", {
              className: "nomaltext",
              innerHTML: participant.Company,
            });
            pContainer.appendChild(imgProfile);
            pContainer.appendChild(nameContainer);
            pContainer.appendChild(status ? imgStatus : organizerText);
            pContainer.appendChild(createElement("br"));
            pContainer.appendChild(organizationContainer);
            liContainer.appendChild(pContainer);
            ulContainer.appendChild(liContainer);
          });
          participantsElement.appendChild(ulContainer);
          let projectElement = args.element.querySelector("#Projects");
          let ulprojectContainer = createElement("ul", {
            className: "token-input-list",
          });
          args.data.Projects.forEach(function (projects) {
            let liContainer = createElement("li", {
              className: "token-input-token",
            });
            let pContainer = createElement("p");
            let imgProfile = createElement("img", {
              className: "small-profile",
              attrs: {
                src:
                  "https://s3.amazonaws.com/brunicoxchangeprojects/" +
                  projects.ImageLocation,
              },
            });
            let aContainer = createElement("a", {
              innerHTML: projects.ProjectName,
              attrs: { href: "#" },
            });
            pContainer.appendChild(imgProfile);
            pContainer.appendChild(aContainer);
            liContainer.appendChild(pContainer);
            ulprojectContainer.appendChild(liContainer);
          });
          projectElement.appendChild(ulprojectContainer);
        }

        document.querySelectorAll(".joinMeetingButton").forEach(
          (el) =>
            (el.onclick = function () {
              var URL = document.getElementById("XchangeMeetingURL").value;
              if (URL === URL_NOT_AVAILABLE) {
                alert(URL_NOT_AVAILABLE_MESSAGE);
              } else {
                if (
                  document.getElementById("Location").value ===
                  MEETING_LOCATIONS.DELEGATE_LOUNGE
                ) {
                  logAction();
                }

                let meetingURL =
                  document.getElementById("Location").value ===
                  MEETING_LOCATIONS.PERSONAL_ROOM
                    ? getPersonalConferanceURL(URL)
                    : URL;
                window.open(meetingURL);
              }
            })
        );
      }
    } else {
      args.cancel = true;
    }
  };

  return (
    <ScheduleComponent
      width="100%"
      height="430px"
      minDate={new Date(2021, 0, 11)}
      eventSettings={{ dataSource: filteredEvents, enableTooltip: true }}
      currentView="Week"
      editorTemplate={editorTemplate}
      popupOpen={onPopupOpen}
      dragStart={onDragStart}
      ref={scheduleRef}
    >
      <Inject services={[Day, Week, Month, Agenda, Resize, DragAndDrop]} />
      <ResourcesDirective>
        <ResourceDirective
          field="HostId"
          title="Host"
          name="Host"
          allowMultiple={true}
          dataSource={ResourceData}
          textField="Text"
          idField="Id"
          colorField="Color"
        />
      </ResourcesDirective>
      <ViewsDirective>
        <ViewDirective option="Day" />
        <ViewDirective option="Week" />
        <ViewDirective option="Month" />
        <ViewDirective option="Agenda" />
      </ViewsDirective>
    </ScheduleComponent>
  );
}

const editorTemplate = (props) => {
  return props !== undefined ? (
    <>
      <table
        id="custom-session-editor"
        className="custom-event-editor readonlymode"
        style={{ width: "100%", cellpadding: "5" }}
      >
        <tbody>
          <tr>
            <td className="e-textlabel"></td>
            <td className="editorcolumn" style={{ colSpan: "4" }}>
              <input
                id="SessionSummary"
                readOnly="readonly"
                className="e-field e-input"
                type="text"
                name="Subject"
                style={{ width: "100%" }}
                tabIndex="-1"
              />
            </td>
          </tr>

          <tr>
            <td className="e-textlabel">Date</td>
            <td className="editorcolumn" style={{ colSpan: "4" }}>
              <input
                id="SessionDate"
                readOnly="readonly"
                className="e-field"
                type="text"
                name="SessionDate"
                style={{ width: "100%" }}
              />
            </td>
          </tr>

          <tr>
            <td colSpan="4">
              <table className="e-textlabel" style={{ display: "inline" }}>
                <tbody>
                  <tr>
                    <td className="e-textlabel twocolumnlabel">Start</td>
                    <td className="editorcolumn" style={{ colSpan: "4" }}>
                      <input
                        id="SessionStartTime"
                        readOnly="readonly"
                        className="e-field"
                        type="text"
                        name="SessionStartTime"
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
              <table className="e-textlabel" style={{ display: "inline" }}>
                <tbody>
                  <tr>
                    <td className="e-textlabel twocolumnlabel">End</td>
                    <td className="editorcolumn" style={{ colSpan: "4" }}>
                      <input
                        id="SessionEndTime"
                        readOnly="readonly"
                        className="e-field"
                        type="text"
                        name="SessionEndTime"
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </td>
          </tr>
          <tr>
            <td className="e-textlabel" style={{ verticalAlign: "top" }}>
              Room
            </td>
            <td className="editorcolumn tdlocation" style={{ colSpan: "4" }}>
              <div style={{ display: "inline" }}>
                <input
                  id="SessionXchangeMeetingURL"
                  readOnly="readonly"
                  className="e-field e-input joinmeetingURL"
                  type="text"
                  name="XchangeMeetingURL"
                  style={{ width: "180px", display: "inline" }}
                  tabIndex="-1"
                />
                <Button className="copybuttonwithicon" id="SessionBtnCopyURL">
                  <i className="far fa-copy"></i>
                </Button>
                <Button
                  type="button"
                  className="joinMeetingButton"
                  id="SessionJoinMeetingButton"
                >
                  {" "}
                  Join Sessiom{" "}
                </Button>
              </div>
            </td>
          </tr>
          <tr>
            <td className="e-textlabel" style={{ verticalAlign: "top" }}>
              Description
            </td>
            <td className="editorcolumn" style={{ colSpan: "4" }}>
              <div
                id="SessionDescription"
                name="Notes"
                data-name="Notes"
                style={{
                  width: "100%",
                }}
              ></div>
            </td>
          </tr>
        </tbody>
      </table>

      <table
        id="custom-event-editor"
        className="custom-event-editor readonlymode"
        style={{ width: "100%", cellpadding: "5" }}
      >
        <tbody>
          <tr>
            <td className="e-textlabel">Title</td>
            <td className="editorcolumn" style={{ colSpan: "4" }}>
              <input
                id="Summary"
                readOnly="readonly"
                className="e-field e-input"
                type="text"
                name="Subject"
                style={{ width: "100%" }}
                tabIndex="-1"
              />
            </td>
          </tr>
          <tr>
            <td className="e-textlabel" style={{ verticalAlign: "top" }}>
              Participants
            </td>
            <td className="editorcolumn" style={{ colSpan: "4" }}>
              <div id="Participants"></div>
            </td>
          </tr>
          <tr>
            <td colSpan="4">
              <table className="e-textlabel" style={{ display: "inline" }}>
                <tbody>
                  <tr>
                    <td className="e-textlabel twocolumnlabel">From</td>
                    <td className="editorcolumn" style={{ colSpan: "4" }}>
                      <DateTimePickerComponent
                        id="StartTime"
                        format="MMMM dd, yyyy hh:mm a"
                        data-name="StartTime"
                        value={new Date(props.startTime || props.StartTime)}
                        className="e-field"
                        tabIndex="-1"
                      ></DateTimePickerComponent>
                    </td>
                  </tr>
                </tbody>
              </table>
              <table className="e-textlabel" style={{ display: "inline" }}>
                <tbody>
                  <tr>
                    <td className="e-textlabel twocolumnlabel">To</td>
                    <td className="editorcolumn" style={{ colSpan: "4" }}>
                      <DateTimePickerComponent
                        id="EndTime"
                        format="MMMM dd, yyyy hh:mm a"
                        data-name="EndTime"
                        value={new Date(props.endTime || props.EndTime)}
                        className="e-field"
                        tabIndex="-1"
                      ></DateTimePickerComponent>
                    </td>
                  </tr>
                </tbody>
              </table>
            </td>
          </tr>
          <tr>
            <td className="e-textlabel" style={{ verticalAlign: "top" }}>
              Projects
            </td>
            <td className="editorcolumn" style={{ colSpan: "4" }}>
              <div id="Projects"></div>
            </td>
          </tr>
          <tr>
            <td className="e-textlabel" style={{ verticalAlign: "top" }}>
              Location
            </td>
            <td className="editorcolumn tdlocation" style={{ colSpan: "4" }}>
              <DropDownListComponent
                id="Location"
                placeholder="Choose location"
                data-name="Location"
                className="e-field"
                style={{ width: "200px" }}
                value={props.Location || null}
                dataSource={[
                  "Delegate Lounge",
                  "Meeting Suite/Pavilion",
                  "Personal Conference Room",
                ]}
                tabIndex="-1"
              ></DropDownListComponent>
              <div style={{ display: "inline" }}>
                <input
                  id="XchangeMeetingURL"
                  readOnly="readonly"
                  className="e-field e-input joinmeetingURL"
                  type="text"
                  name="XchangeMeetingURL"
                  style={{ width: "180px", display: "inline" }}
                  tabIndex="-1"
                />
                <Button className="copybuttonwithicon" id="btnCopyURL">
                  <i className="far fa-copy"></i>
                </Button>
                <Button
                  type="button"
                  className="joinMeetingButton"
                  id="joinMeetingButton"
                >
                  {" "}
                  Join Meeting{" "}
                </Button>
              </div>
            </td>
          </tr>
          <tr>
            <td className="e-textlabel" style={{ verticalAlign: "top" }}>
              Description
            </td>
            <td className="editorcolumn" style={{ colSpan: "4" }}>
              <textarea
                id="Description"
                className="e-field e-input"
                name="Notes"
                data-name="Notes"
                rows={3}
                cols={50}
                style={{
                  width: "100%",
                  height: "60px !important",
                  resize: "vertical",
                }}
              ></textarea>
            </td>
          </tr>
        </tbody>
      </table>
    </>
  ) : (
    <div></div>
  );
};

const onDragStart = (args) => {
  args.cancel = true;
};

export default ScheduleView;
